import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { auth } from '../config/firebase';

export default function MagicLink() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'signing_in' | 'success' | 'error' | 'email_required'>(
    'signing_in'
  );
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    async function handleSignIn() {
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        setStatus('error');
        setErrorMessage('This is not a valid sign-in link.');
        return;
      }

      // Try to get email from localStorage
      let emailForSignIn = window.localStorage.getItem('emailForSignIn');

      if (!emailForSignIn) {
        // If email is not in localStorage, ask user to provide it
        setStatus('email_required');
        return;
      }

      try {
        await signInWithEmailLink(auth, emailForSignIn, window.location.href);
        window.localStorage.removeItem('emailForSignIn');
        setStatus('success');

        // Redirect after short delay
        setTimeout(() => {
          const redirect = searchParams.get('redirect') || '/portal';
          navigate(redirect);
        }, 2000);
      } catch (err: any) {
        console.error('Magic link sign-in error:', err);
        setStatus('error');

        switch (err.code) {
          case 'auth/expired-action-code':
            setErrorMessage('This sign-in link has expired. Please request a new one.');
            break;
          case 'auth/invalid-action-code':
            setErrorMessage('This sign-in link is invalid or has already been used.');
            break;
          case 'auth/invalid-email':
            setErrorMessage('The email address is invalid.');
            break;
          default:
            setErrorMessage('Failed to sign in. Please try again or contact support.');
        }
      }
    }

    handleSignIn();
  }, [searchParams, navigate]);

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setStatus('signing_in');
    setErrorMessage('');

    try {
      await signInWithEmailLink(auth, email.toLowerCase().trim(), window.location.href);
      setStatus('success');

      setTimeout(() => {
        const redirect = searchParams.get('redirect') || '/portal';
        navigate(redirect);
      }, 2000);
    } catch (err: any) {
      console.error('Magic link sign-in error:', err);
      setStatus('error');

      switch (err.code) {
        case 'auth/expired-action-code':
          setErrorMessage('This sign-in link has expired. Please request a new one.');
          break;
        case 'auth/invalid-action-code':
          setErrorMessage('This sign-in link is invalid or has already been used.');
          break;
        case 'auth/invalid-email':
          setErrorMessage('The email address is invalid or does not match the sign-in request.');
          break;
        default:
          setErrorMessage('Failed to sign in. Please try again or contact support.');
      }
    }
  }

  if (status === 'signing_in') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-6xl mb-4">🔐</div>
          <h1 className="text-2xl font-semibold text-primary mb-2">Signing You In...</h1>
          <p className="text-secondary">Please wait while we verify your magic link.</p>
        </div>
      </div>
    );
  }

  if (status === 'email_required') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-6">
            <div className="text-6xl mb-4">📧</div>
            <h1 className="text-2xl font-semibold text-primary mb-2">Confirm Your Email</h1>
            <p className="text-secondary">
              Please enter the email address you used to request the sign-in link.
            </p>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleEmailSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Complete Sign In
            </button>
          </form>

          <p className="text-sm text-center text-secondary mt-6">
            Having trouble?{' '}
            <a href="mailto:support@dealvelocity.co.za" className="text-blue-600 hover:underline">
              Contact support
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
          <div className="text-6xl mb-4">❌</div>
          <h1 className="text-2xl font-semibold text-primary mb-4">Sign In Failed</h1>
          <p className="text-secondary mb-6">{errorMessage}</p>

          <div className="space-y-3">
            <Link
              to="/"
              className="block w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 transition-colors"
            >
              Go to Homepage
            </Link>
            <a
              href="mailto:support@dealvelocity.co.za"
              className="block w-full border border-gray-300 text-secondary py-2 px-4 rounded-md font-medium hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="text-6xl mb-4">✅</div>
        <h1 className="text-2xl font-semibold text-primary mb-4">Successfully Signed In!</h1>
        <p className="text-secondary mb-6">Redirecting you to your account...</p>

        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    </div>
  );
}
